import { ColorScheme } from "@/context/ThemeContext";
import { getLogger } from "@/utils/log";

const logger = getLogger("system:preference");

/**
 * Detects the system's color scheme preference
 *
 * This function checks the user's system preference for dark or light mode
 * using the prefers-color-scheme media query. It handles cases where window.matchMedia
 * is not available (like in server-side rendering or test environments).
 *
 * @returns {ColorScheme} ColorScheme.DARK or ColorScheme.LIGHT based on system preference
 */
export function getSystemColorScheme(): ColorScheme {
  // Handle server-side rendering or environments without window
  if (typeof window === "undefined") {
    return ColorScheme.LIGHT;
  }

  // Handle environments where matchMedia is not available (like some test environments)
  if (!window.matchMedia) {
    // Log a warning but don't crash, default to light mode
    if (import.meta.env.MODE !== "test") {
      logger.log(
        "window.matchMedia is not available, defaulting to light color scheme",
      );
    }
    return ColorScheme.LIGHT;
  }

  try {
    // Check if the system prefers dark mode
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? ColorScheme.DARK
      : ColorScheme.LIGHT;
  } catch (error) {
    // Handle any unexpected errors with the matchMedia API
    logger.error(error, { context: "detecting system color scheme" });
    return ColorScheme.LIGHT;
  }
}
